/*
 * PDP Shop The Look
 * -----------------
 * Orchestrates the Shop The Look drawer: lazily loads the complementary products
 * (same Search & Discovery source as "Pairs well with"), hydrates each row with the
 * product's native quick-buy form, and handles add-to-cart through the theme's own
 * event contract (cart:change) without auto-opening the cart drawer, so the look
 * drawer can show its "added" panel with a back action.
 *
 * theme.js is a module bundle, so nothing is borrowed from its internals — only the
 * custom elements it registers (x-drawer, product-form, variant-picker, ...) and the
 * events it documents (cart:prepare-bundled-sections, variant:add, cart:change).
 */

const pageCache = new Map();

async function fetchDocument(url) {
  if (!pageCache.has(url)) {
    pageCache.set(url, fetch(url).then((response) => {
      if (!response.ok) {
        pageCache.delete(url);
        throw new Error(`PDP STL: fetch failed for ${url}`);
      }
      return response.text();
    }));
  }
  const text = await pageCache.get(url);
  return new DOMParser().parseFromString(text, 'text/html');
}

function extractQuickBuyContent(scope) {
  const template = scope.getElementById('quick-buy-content');
  if (!template) {
    return null;
  }
  const content = document.importNode(template.content, true);
  content.querySelectorAll('noscript').forEach((noScript) => noScript.remove());
  return content;
}

class PdpShopTheLook extends HTMLElement {
  connectedCallback() {
    // The drawer is re-appended to <body> when first opened, which disconnects
    // and reconnects this element — bind everything exactly once or every
    // reopen would stack duplicate listeners and duplicate hydrated content.
    if (this.bound) {
      return;
    }
    this.bound = true;

    this.drawer = this.closest('x-drawer');
    this.lookPanel = this.querySelector('[data-pdp-stl-panel="look"]');
    this.addedPanel = this.querySelector('[data-pdp-stl-panel="added"]');

    // Theme editor only: a section reload removes an open drawer without
    // firing dialog:after-hide — clear the sticky-bar-hiding class so it
    // cannot stick for the rest of the editor session. Inert on storefront.
    document.addEventListener('shopify:section:unload', () => document.body.classList.remove('pdp-stl-open'));

    this.drawer?.addEventListener('dialog:before-show', () => this.init(), { once: true });

    // Keep the theme's sticky ATC bar out of the way while the look drawer is
    // open, and always reopen on the look list rather than a stale added panel.
    this.drawer?.addEventListener('dialog:before-show', () => document.body.classList.add('pdp-stl-open'));
    this.drawer?.addEventListener('dialog:after-hide', () => {
      document.body.classList.remove('pdp-stl-open');
      this.showPanel('look');
    });

    // Capture phase: runs before the theme's <product-form> submit handler, so the
    // cart drawer's auto-open (keyed to baseEvent "variant:add") never triggers.
    this.addEventListener('submit', (event) => this.onSubmit(event), true);

    this.addEventListener('click', (event) => {
      const addProxy = event.target.closest('[data-pdp-stl-add]');
      if (addProxy) {
        // Forward to the real (hidden) submit button; the form submit then
        // bubbles into our capture-phase handler below.
        const row = addProxy.closest('[data-pdp-stl-row]');
        row?.querySelector('[data-pdp-stl-form] .buy-buttons button[type="submit"]')?.click();
      } else if (event.target.closest('[data-pdp-stl-back]')) {
        this.showPanel('look');
      } else if (event.target.closest('[data-pdp-stl-viewbag]')) {
        this.openCartDrawer();
      }
    });
  }

  async init() {
    const loading = this.querySelector('[data-pdp-stl-loading]');
    loading?.removeAttribute('hidden');

    this.hydrateRow(this.querySelector('[data-pdp-stl-row][data-current]'));

    try {
      const url = `${Shopify.routes.root}recommendations/products` +
        `?product_id=${this.getAttribute('product-id')}` +
        `&limit=${this.getAttribute('companions-limit') || 4}` +
        `&section_id=pdp-stl-items&intent=complementary`;
      const doc = await fetchDocument(url);
      const items = doc.querySelector('[data-pdp-stl-items]');
      const container = this.querySelector('[data-pdp-stl-companions]');

      if (items && items.children.length > 0) {
        const rows = Array.from(items.querySelectorAll('[data-pdp-stl-row]')).map((row) => document.importNode(row, true));
        container.append(...rows);
        rows.forEach((row) => this.hydrateRow(row));
      } else {
        this.querySelector('[data-pdp-stl-empty]')?.removeAttribute('hidden');
        // Resolved at use time: the chip lives inside the main
        // product-rerender and a cached node could go stale.
        const chip = document.getElementById(this.getAttribute('chip-id'));
        if (chip) {
          chip.hidden = true;
        }
      }
    } catch (error) {
      // Network hiccup: the drawer still works for the current product.
      console.warn(error);
    } finally {
      loading?.setAttribute('hidden', '');
    }
  }

  async hydrateRow(row) {
    if (!row) {
      return;
    }
    try {
      let content;
      if (row.hasAttribute('data-current')) {
        content = extractQuickBuyContent(document);
      } else {
        const doc = await fetchDocument(`${Shopify.routes.root}products/${row.getAttribute('data-product-handle')}`);
        content = extractQuickBuyContent(doc);
      }
      if (!content) {
        return;
      }
      const slot = row.querySelector('[data-pdp-stl-form]');
      slot.replaceChildren(content);
      slot.hidden = false;
      this.wireProxy(row, slot);
      window.Shopify?.PaymentButton?.init();
    } catch (error) {
      console.warn(error);
    }
  }

  wireProxy(row, slot) {
    // The real buy-buttons block (form included) stays inside the slot, fully
    // owned by the theme's rerender cycle — it is only hidden by CSS. The
    // head shows our proxy button, kept in sync READ-ONLY: the observer
    // copies disabled state + label from the real button after each theme
    // swap and never touches theme DOM, so size changes cost nothing extra
    // and the form wiring can never detach.
    const proxy = row.querySelector('[data-pdp-stl-add]');
    if (!proxy) {
      return;
    }
    const sync = () => {
      const real = slot.querySelector('.buy-buttons button[type="submit"]');
      proxy.disabled = !real || real.disabled;
      const label = proxy.querySelector('[data-pdp-stl-add-label]');
      if (real && label) {
        label.textContent = real.textContent.trim() || 'Add to cart';
      }
    };
    sync();
    if (!row.stlSyncObserver) {
      row.stlSyncObserver = new MutationObserver(sync);
      row.stlSyncObserver.observe(slot, { childList: true, subtree: true });
    }
  }

  async onSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Busy guard lives on the ROW (a stable node): a size change mid-request
    // fully replaces the form subtree, so button-level flags could detach and
    // let a second tap double-POST.
    const row = form.closest('[data-pdp-stl-row]');
    if (row?.dataset.stlBusy === 'true') {
      return;
    }
    if (row) {
      row.dataset.stlBusy = 'true';
    }
    const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
    const proxy = row?.querySelector('[data-pdp-stl-add]');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    proxy?.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      // Same sequence as the theme's ProductForm, with a distinct baseEvent so the
      // cart drawer updates its content and count but does not auto-open.
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const formData = new FormData(form);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${form.id.value}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const responseJson = await response.json();

      if (response.ok) {
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];
        const items = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];

        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items, cart: cartContent, onSuccessDo: null }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'pdp-stl:add', onSuccessDo: null, cart: cartContent }
        }));

        this.showAdded(items[0]);
      } else {
        this.showRowError(form, responseJson['description'] || responseJson['message'] || 'Unable to add this item.');
      }
    } catch (error) {
      this.showRowError(form, 'Unable to add this item. Please try again.');
      console.warn(error);
    } finally {
      if (row) {
        delete row.dataset.stlBusy;
      }
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
      proxy?.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  showAdded(item) {
    const summary = this.querySelector('[data-pdp-stl-added-item]');
    if (summary && item) {
      summary.replaceChildren();

      if (item.image) {
        const image = document.createElement('img');
        image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=176`;
        image.alt = '';
        image.width = 88;
        image.loading = 'lazy';
        image.className = 'pdp-stl-added__image';
        summary.append(image);
      }

      const info = document.createElement('div');
      info.className = 'v-stack gap-1';
      const title = document.createElement('p');
      title.className = 'product-title h6';
      title.textContent = item.product_title || '';
      info.append(title);
      if (item.variant_title) {
        const variant = document.createElement('p');
        variant.className = 'text-sm text-subdued';
        variant.textContent = item.variant_title;
        info.append(variant);
      }
      summary.append(info);
    }
    this.showPanel('added');
  }

  showRowError(form, message) {
    const row = form.closest('[data-pdp-stl-row]');
    if (!row) {
      return;
    }
    row.querySelector('.banner--error')?.remove();
    const banner = document.createElement('div');
    banner.className = 'banner banner--error';
    banner.setAttribute('role', 'alert');
    banner.textContent = message;
    row.append(banner);
    setTimeout(() => banner.remove(), 4000);
  }

  showPanel(name) {
    this.lookPanel.hidden = name !== 'look';
    this.addedPanel.hidden = name !== 'added';
    if (name === 'added') {
      this.querySelector('[data-pdp-stl-back]')?.focus();
    }
  }

  async openCartDrawer() {
    await this.drawer?.hide?.();
    document.getElementById('cart-drawer')?.show?.();
  }
}

if (!window.customElements.get('pdp-shop-the-look')) {
  window.customElements.define('pdp-shop-the-look', PdpShopTheLook);
}
